import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext(null);

export const LocationProvider = ({ children }) => {
    // Default coordinates (Abu Dhabi)
    const DEFAULT_LAT = 24.4539;
    const DEFAULT_LNG = 54.3773;

    const [latitude, setLatitude] = useState(DEFAULT_LAT);
    const [longitude, setLongitude] = useState(DEFAULT_LNG);
    const [selectedState, setSelectedState] = useState(null);
    const [isLocationLoading, setIsLocationLoading] = useState(true);
    const [geolocationError, setGeolocationError] = useState(null);
    const [hasRealLocation, setHasRealLocation] = useState(false);

    // Helper function to check if coordinates are default values
    const isDefaultCoordinates = (lat, lng) => {
        if (!lat || !lng) return true;
        return Math.abs(lat - DEFAULT_LAT) < 0.0001 && Math.abs(lng - DEFAULT_LNG) < 0.0001;
    };

    // Helper function to log real location only
    const logRealLocation = (lat, lng) => {
        const timestamp = new Date().toLocaleTimeString();
        // console.log(`[${timestamp}] 📍 Real Location Obtained:`, {
        //     latitude: lat,
        //     longitude: lng
        // });
    };

    // Initialize location on mount
    useEffect(() => {
        let isMounted = true;
        const startTime = Date.now();

        const initializeLocation = async () => {
            setIsLocationLoading(true);
            setGeolocationError(null);
            setHasRealLocation(false);

            // Step 1: Check for cached geolocation from previous session (FAST - instant)
            const cachedGeo = localStorage.getItem('lastGeolocation');
            if (cachedGeo) {
                try {
                    const geoData = JSON.parse(cachedGeo);
                    const age = Date.now() - geoData.timestamp;
                    const oneHour = 60 * 60 * 1000; // 1 hour
                    
                    if (age < oneHour && geoData.latitude && geoData.longitude && !isDefaultCoordinates(geoData.latitude, geoData.longitude)) {
                        if (isMounted) {
                            // Log real location from cache
                            logRealLocation(geoData.latitude, geoData.longitude);
                            setLatitude(geoData.latitude);
                            setLongitude(geoData.longitude);
                            setHasRealLocation(true);
                            setIsLocationLoading(false);
                            
                            // Still try fresh geolocation in background immediately (non-blocking)
                            // Use requestIdleCallback for non-blocking, or immediate if not available
                            if (window.requestIdleCallback) {
                                requestIdleCallback(() => {
                                    if (isMounted) {
                                        getFreshGeolocation();
                                    }
                                });
                            } else {
                                // Fallback: use setTimeout with 0 delay for immediate execution
                                setTimeout(() => {
                                    if (isMounted) {
                                        getFreshGeolocation();
                                    }
                                }, 0);
                            }
                            return;
                        }
                    }
                } catch (e) {
                    // Error parsing cached geolocation
                }
            }

            // Step 2: Check localStorage for user-selected state
            const savedState = localStorage.getItem('selectedState');
            if (savedState) {
                try {
                    const parsedState = JSON.parse(savedState);
                    const lat = parseFloat(parsedState.latitude || parsedState.lat || DEFAULT_LAT);
                    const lng = parseFloat(parsedState.longitude || parsedState.lng || DEFAULT_LNG);

                    // Only use saved state if it's a user-selected state (has name/id) or not default coords
                    if (parsedState.name || parsedState.id || !isDefaultCoordinates(lat, lng)) {
                        if (isMounted) {
                            setSelectedState(parsedState);
                            setLatitude(lat);
                            setLongitude(lng);
                            setHasRealLocation(!isDefaultCoordinates(lat, lng));
                            setIsLocationLoading(false);
                            
                            // Still try fresh geolocation in background immediately (non-blocking)
                            // Use requestIdleCallback for non-blocking, or immediate if not available
                            if (window.requestIdleCallback) {
                                requestIdleCallback(() => {
                                    if (isMounted) {
                                        getFreshGeolocation();
                                    }
                                });
                            } else {
                                // Fallback: use setTimeout with 0 delay for immediate execution
                                setTimeout(() => {
                                    if (isMounted) {
                                        getFreshGeolocation();
                                    }
                                }, 0);
                            }
                            return;
                        }
                    } else {
                        localStorage.removeItem('selectedState');
                    }
                } catch (error) {
                    // Error parsing saved state
                }
            }

            // Step 3: Try fresh geolocation
            getFreshGeolocation();
        };

        // Function to get fresh geolocation with retry
        const getFreshGeolocation = (retryCount = 0) => {
            if (!navigator.geolocation) {
                if (isMounted) {
                    setGeolocationError('Geolocation is not supported by this browser');
                    setHasRealLocation(false);
                    setIsLocationLoading(false);
                }
                return;
            }

            const geoStartTime = Date.now();

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (isMounted) {
                        const lat = position.coords.latitude;
                        const lng = position.coords.longitude;
                        const elapsed = Date.now() - startTime;
                        const geoElapsed = Date.now() - geoStartTime;

                        // Validate coordinates
                        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng) && !isDefaultCoordinates(lat, lng)) {
                            // Log real location only
                            logRealLocation(lat, lng);
                            
                            setLatitude(lat);
                            setLongitude(lng);
                            setHasRealLocation(true);
                            setIsLocationLoading(false);
                            setGeolocationError(null);

                            // Cache successful geolocation for next time
                            const geolocationData = {
                                latitude: lat,
                                longitude: lng,
                                timestamp: Date.now(),
                                source: 'geolocation'
                            };
                            localStorage.setItem('lastGeolocation', JSON.stringify(geolocationData));
                        } else {
                            if (retryCount < 2) {
                                // Retry immediately without delay
                                getFreshGeolocation(retryCount + 1);
                            } else {
                                setHasRealLocation(false);
                                setIsLocationLoading(false);
                            }
                        }
                    }
                },
                (error) => {
                    let errorMsg = 'Unknown error';
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMsg = 'Permission denied';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMsg = 'Position unavailable';
                            break;
                        case error.TIMEOUT:
                            errorMsg = 'Timeout';
                            break;
                    }

                    // Retry on timeout immediately (no delay)
                    if (error.code === error.TIMEOUT && retryCount < 2) {
                        getFreshGeolocation(retryCount + 1);
                        return; // Exit early to avoid setting default
                    } else {
                        // Check for cached geolocation as fallback
                        const cachedGeo = localStorage.getItem('lastGeolocation');
                        if (cachedGeo) {
                            try {
                                const geoData = JSON.parse(cachedGeo);
                                const age = Date.now() - geoData.timestamp;
                                const oneHour = 60 * 60 * 1000;
                                
                                if (age < oneHour && geoData.latitude && geoData.longitude) {
                                    // Log real location from cache
                                    logRealLocation(geoData.latitude, geoData.longitude);
                                    setLatitude(geoData.latitude);
                                    setLongitude(geoData.longitude);
                                    setHasRealLocation(true);
                                    setIsLocationLoading(false);
                                    return;
                                }
                            } catch (e) {
                                // Error parsing fallback cache
                            }
                        }

                        setGeolocationError(errorMsg);
                        setHasRealLocation(false);
                        setIsLocationLoading(false);
                    }
                },
                {
                    timeout: 60000, // 60 seconds - effectively no limit, wait for real location
                    enableHighAccuracy: true, // Try high accuracy first for better/faster results
                    maximumAge: 0 // Always get fresh position, but accept any cached if available
                }
            );
        };

        initializeLocation();

        return () => {
            isMounted = false;
        };
    }, []); // Only run once on mount

    // Function to update location when user selects a state manually
    const updateLocation = useCallback((state) => {
        const lat = parseFloat(state.latitude || state.lat || DEFAULT_LAT);
        const lng = parseFloat(state.longitude || state.lng || DEFAULT_LNG);

        setSelectedState(state);
        setLatitude(lat);
        setLongitude(lng);
        setHasRealLocation(!isDefaultCoordinates(lat, lng));

        // Save to localStorage
        localStorage.setItem('selectedState', JSON.stringify(state));
    }, []);

    // Function to manually set coordinates (if needed)
    const setCoordinates = useCallback((lat, lng) => {
        setLatitude(lat);
        setLongitude(lng);
        setHasRealLocation(!isDefaultCoordinates(lat, lng));
    }, []);


    const value = {
        latitude,
        longitude,
        selectedState,
        isLocationLoading,
        geolocationError,
        hasRealLocation,
        updateLocation,
        setCoordinates,
        setSelectedState
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};

// Custom hook to use location context
export const useLocation = () => {
    const context = useContext(LocationContext);
    if (!context) {
        throw new Error('useLocation must be used within a LocationProvider');
    }
    return context;
};

export default LocationContext;
